import { AssessmentType, type CyberAssessmentSelectDto, type SurveyListSelectDto } from '@@types/api.types';
import { useApi } from '@hooks/api.hook';
import { getTechnologyCyberAssessmentsQueryOptions } from '@queries/cyber-assessment.query';
import { useGetCurrentUser, useRoleEndpointPrefix } from '@queries/users.query';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type HomeDraftItem, buildHomeDraftItems } from './home-technology-surveys.util';

const isNonEmptyString = (value?: string): value is string => {
  return typeof value === 'string' && value.length > 0;
};

type UseHomeTechnologyDraftItemsParams = {
  draftSurveys: SurveyListSelectDto[];
};

type UseHomeTechnologyDraftItemsReturn = {
  draftItems: Array<HomeDraftItem<SurveyListSelectDto, CyberAssessmentSelectDto>>;
};

export const useHomeTechnologyDraftItems = (
  params: UseHomeTechnologyDraftItemsParams,
): UseHomeTechnologyDraftItemsReturn => {
  const { draftSurveys } = params;

  const { apiGet } = useApi();
  const { isExpert, isInnovator, isIntel } = useGetCurrentUser();
  const roleEndpointPrefix = useRoleEndpointPrefix();
  const cyberDraftTechnologyIds = useMemo(() => {
    return [
      ...new Set(
        draftSurveys
          .filter((survey) => survey.assessmentType === AssessmentType.CYBER)
          .map((survey) => survey.technology?.id)
          .filter(isNonEmptyString),
      ),
    ].slice(0, 10);
  }, [draftSurveys]);
  const canLoadCyberAssessments = Boolean(roleEndpointPrefix) && (isExpert || isInnovator || isIntel);
  const cyberAssessmentQueries = useQueries({
    queries: cyberDraftTechnologyIds.map((technologyId) =>
      getTechnologyCyberAssessmentsQueryOptions({
        apiGet,
        enabled: canLoadCyberAssessments,
        roleEndpointPrefix: roleEndpointPrefix ?? undefined,
        technologyId,
      }),
    ),
  });
  const isCyberDraftLoading = cyberAssessmentQueries.some((query) => query.isLoading);
  const cyberAssessmentData = cyberAssessmentQueries.map((query) => query.data);
  const cyberAssessmentsByTechnologyId = useMemo<Record<string, CyberAssessmentSelectDto[]>>(() => {
    const result: Record<string, CyberAssessmentSelectDto[]> = {};

    cyberDraftTechnologyIds.forEach((technologyId, index) => {
      result[technologyId] = cyberAssessmentData[index] ?? [];
    });

    return result;
  }, [cyberAssessmentData, cyberDraftTechnologyIds]);
  const resolvedDraftSurveys = useMemo(() => {
    if (!isCyberDraftLoading) {
      return draftSurveys;
    }

    return draftSurveys.filter((survey) => survey.assessmentType !== AssessmentType.CYBER);
  }, [draftSurveys, isCyberDraftLoading]);

  const draftItems = useMemo(() => {
    return buildHomeDraftItems({
      cyberAssessmentsByTechnologyId,
      surveys: resolvedDraftSurveys,
    }).slice(0, 2);
  }, [cyberAssessmentsByTechnologyId, resolvedDraftSurveys]);

  return {
    draftItems,
  };
};
